import { X, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { useApp } from "@/contexts/AppContext";

export interface BroadcastMessage {
  id: string;
  subject: {
    de: string;
    en?: string;
    [key: string]: string | undefined;
  };
  message: {
    de: string;
    en?: string;
    [key: string]: string | undefined;
  };
  type: "info" | "warning" | "alert";
  startTime: string;
  endTime: string;
  createdBy: string;
}

interface BroadcastBannerProps {
  message: BroadcastMessage;
  onDismiss: () => void;
}

// NOTE: Broadcast banners are exempt from theming to maintain their warning/alerting character
const BANNER_PALETTE = {
  info: {
    light: {
      background: "#eff6ff",
      border: "#bfdbfe",
      text: "#1e3a8a",
    },
    dark: {
      background: "rgba(30, 58, 138, 0.2)",
      border: "#1e40af",
      text: "#dbeafe",
    },
  },
  warning: {
    light: {
      background: "#fefce8",
      border: "#fef08a",
      text: "#713f12",
    },
    dark: {
      background: "rgba(113, 63, 18, 0.2)",
      border: "#854d0e",
      text: "#fef9c3",
    },
  },
  alert: {
    light: {
      background: "#fef2f2",
      border: "#fecaca",
      text: "#7f1d1d",
    },
    dark: {
      background: "rgba(127, 29, 29, 0.2)",
      border: "#991b1b",
      text: "#fee2e2",
    },
  },
} as const;

export function BroadcastBanner({ message, onDismiss }: BroadcastBannerProps) {
  const { language, activeTheme } = useApp();

  const getLocalizedText = (
    textObj:
      | {
          de: string;
          en?: string;
          [key: string]: string | undefined;
        }
      | undefined
  ): string => {
    if (!textObj) return "";
    return textObj[language] || textObj.de || "";
  };

  const getIcon = () => {
    switch (message.type) {
      case "alert":
        return <AlertCircle className="w-5 h-5" />;
      case "warning":
        return <AlertTriangle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const palette = BANNER_PALETTE[message.type][activeTheme.tone];

  return (
    <div
      className="border-b"
      style={{
        backgroundColor: palette.background,
        borderColor: palette.border,
        color: palette.text,
      }}
    >
      <div className="container mx-auto px-4 py-1.5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
          <div className="flex-1 min-w-0">
            <h4 className="mb-1">{getLocalizedText(message.subject)}</h4>
            <p className="text-sm opacity-90">
              {getLocalizedText(message.message)}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDismiss}
            className="flex-shrink-0 h-8 w-8 p-0 text-current hover:bg-black/10 hover:text-current dark:hover:bg-white/10 dark:hover:text-current"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
